import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import useTags from "hooks-for/tags/use-tags";
import { AgentStateType, IInteractionMetricsSummary, IInteractionsCount } from "@edgetier/watchtower-types";
import { COUNT_RANGE_DAYS } from "../../../live/live.constants";
import { decamelize } from "humps";
import { endOfDay, subMinutes } from "date-fns";
import { groupBy } from "@edgetier/utilities";
import { IInteractionsGetQueryParameters, InteractionState, InteractionType } from "@edgetier/watchtower-types";
import { ILiveSummaryData } from "./live-summary-table.types";
import { indexByGroup } from "utilities/index-by-group";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { roundDownToInterval } from "utilities/round-down-to-interval";
import { SHOW_BLOCKED } from "./live-summary-table.constants";
import { useAgentCounts } from "hooks-for/agents/use-agent-counts";
import { useEffect, useState } from "react";
import { useInteractionMetricsSummaries } from "hooks-for/interaction-metrics/use-interaction-metrics-summaries";
import { useInterval } from "react-use";
import { useSummaryTagGroup } from "../live-summary-tab.utilities";

type TInteractionsByAgent = IInteractionsCount<[agentId: string, tagId: string]>;

const DAY_IN_MINUTES = 1440;

/**
 * Calculate how many interactions available agents have on average.
 * @param interactionsByAgent Array of interactions counted by agent.
 * @returns                   Average interactions per agent.
 */
const calculateMeanAgentCapacity = (agentsCount: number, interactionsByAgent: TInteractionsByAgent[]) => {
    if (agentsCount === 0) {
        return 0;
    }
    return interactionsByAgent.reduce((total, value) => total + value.count, 0) / agentsCount;
};

/**
 * Format interactions count data
 */
const formatCountData = (tagId: string, interactionCountData?: Record<string, IInteractionsCount<string>>) => {
    if (typeof interactionCountData === "undefined") return 0;

    return typeof interactionCountData[tagId] === "undefined" ? 0 : interactionCountData[tagId].count;
};

/**
 * Format metric summaries data
 */
const formatSummariesData = (
    tagId: string,
    getValue: (interactionMetricSummary: IInteractionMetricsSummary<string>) => number | null,
    defaultValue: number | null,
    metricsSummariesData?: Record<string, IInteractionMetricsSummary<string>>
) => {
    if (typeof metricsSummariesData === "undefined") return defaultValue;

    return typeof metricsSummariesData[tagId] === "undefined" ? defaultValue : getValue(metricsSummariesData[tagId]);
};

type TPollParameters = Pick<IInteractionsGetQueryParameters, "endDateTime" | "startDateTime">;

/**
 * Get the start and end time for the current poll.
 * @param minutes How many minutes to request back.
 * @returns       Start and end date parameters.
 */
const getPollParameters = (minutes: number) => ({
    endDateTime: endOfDay(new Date()),
    startDateTime: roundDownToInterval(subMinutes(new Date(), minutes), LIVE_POLL_INTERVAL_MILLISECONDS),
});

/**
 * Update some parameters on an interval so the backend is polled.
 * @param isTagGroupSelected If the user has selected a tag group.
 * @param delayMilliseconds  Time between polls.
 * @param minutes            Minutes to go back as the opening date in the query range.
 * @returns                  Parameters for the current poll.
 */
const usePollParameters = (isTagGroupSelected: boolean, delayMilliseconds: number, minutes: number) => {
    const [parameters, setParameters] = useState<TPollParameters>(isTagGroupSelected ? getPollParameters(minutes) : {});

    // When a tag is selected for the first time the parameters need to be in state.
    useEffect(() => {
        if (isTagGroupSelected) {
            setParameters(getPollParameters(minutes));
        }
    }, [isTagGroupSelected]);

    // Update the parameters to poll the backend.
    useInterval(
        () => setParameters(isTagGroupSelected ? getPollParameters(minutes) : {}),
        isTagGroupSelected ? delayMilliseconds : null
    );

    return parameters;
};

/**
 * See if the poll parameters are initialised. The backend requests should not be made until these are ready, otherwise
 * they could be made with no dates at all and that would be bad for performance.
 * @param parameters Poll parameters.
 * @returns          True if the poll parameters have some dates.
 */
const isInitialised = (parameters: Partial<IInteractionsGetQueryParameters>) => {
    return parameters.startDateTime instanceof Date;
};

/**
 * Hook to generate live summary data
 */
export const useLiveSummaryData = () => {
    const [tagGroupSelectedId] = useSummaryTagGroup(undefined);
    const isTagGroupSelected = typeof tagGroupSelectedId === "string" && tagGroupSelectedId.length > 0;

    const serviceLevelAgreementParameters = usePollParameters(
        isTagGroupSelected,
        LIVE_POLL_INTERVAL_MILLISECONDS * 4,
        30
    );

    const countRangeDaysInMinutes = COUNT_RANGE_DAYS * DAY_IN_MINUTES;
    const interactionDateParameters = usePollParameters(
        isTagGroupSelected,
        LIVE_POLL_INTERVAL_MILLISECONDS,
        countRangeDaysInMinutes
    );

    const isReady = isInitialised(interactionDateParameters) && isInitialised(serviceLevelAgreementParameters);

    // Request tags from the selected tag group
    const { data: tags, isLoading: isTagsLoading } = useTags(
        { tagGroupId: tagGroupSelectedId },
        { enabled: isTagGroupSelected }
    );

    const groupParameters = {
        groupBy: decamelize("tagId"),
        interactionType: [InteractionType.Chat],
        tagGroupId: tagGroupSelectedId,
    };

    // Request all interactions count grouped by tagId
    const totalConversationsInteractionState = [
        InteractionState.Handling,
        InteractionState.Queuing,
        InteractionState.WrapUp,
        ...(SHOW_BLOCKED ? [InteractionState.Blocked] : []),
    ];

    const options = { enabled: isReady && isTagGroupSelected, keepPreviousData: true };
    const select = indexByGroup;

    const { data: totalConversationsOpen, isLoading: isTotalConversationsOpenLoading } = useInteractionsCounts(
        { interactionState: totalConversationsInteractionState, ...groupParameters, ...interactionDateParameters },
        { ...options, enabled: isReady && isTagGroupSelected && SHOW_BLOCKED, select }
    );

    const { data: activeInteractionsCount, isLoading: isActiveInteractionsCountLoading } = useInteractionsCounts(
        { interactionState: [InteractionState.Handling], ...groupParameters, ...interactionDateParameters },
        { ...options, select }
    );

    // This may be disabled by the feature flag.
    const { data: snoozedInteractionsCount, isLoading: isSnoozedInteractionsCountLoading } = useInteractionsCounts(
        { interactionState: [InteractionState.Blocked], ...groupParameters, ...interactionDateParameters },
        { ...options, enabled: isReady && SHOW_BLOCKED && isTagGroupSelected, select }
    );

    const { data: queuingInteractionsCount, isLoading: isQueuingInteractionsCountLoading } = useInteractionsCounts(
        { interactionState: [InteractionState.Queuing], ...groupParameters, ...interactionDateParameters },
        { ...options, select }
    );

    // Request all interactions metrics summaries grouped by tagId
    const { data: maximumDuration, isLoading: isMaximumDurationLoading } = useInteractionMetricsSummaries(
        { interactionState: [InteractionState.Handling], ...groupParameters, ...interactionDateParameters },
        { ...options, select }
    );

    const { data: averageQueueTime, isLoading: isAverageQueueTimeLoading } = useInteractionMetricsSummaries(
        { interactionState: [InteractionState.Queuing], ...groupParameters, ...interactionDateParameters },
        { ...options, select }
    );

    const { data: maximumQueueTime, isLoading: isMaximumQueueTimeLoading } = useInteractionMetricsSummaries(
        { interactionState: [InteractionState.Queuing], ...groupParameters, ...interactionDateParameters },
        { ...options, select }
    );

    const { data: serviceLevelAgreement, isLoading: isServiceLevelAgreementLoading } = useInteractionMetricsSummaries(
        { ...serviceLevelAgreementParameters, ...groupParameters },
        { ...options, select }
    );

    // Request all agent counts grouped by tagId
    const { data: availableAgents, isLoading: isAvailableAgentsLoading } = useAgentCounts(
        { agentStateType: AgentStateType.Available, ...groupParameters },
        {
            ...options,
            refetchInterval: isTagGroupSelected ? LIVE_POLL_INTERVAL_MILLISECONDS : false,
            select,
        }
    );

    const { data: unavailableAgents, isLoading: isUnavailableAgentsLoading } = useAgentCounts(
        { agentStateType: AgentStateType.Unavailable, ...groupParameters },
        {
            ...options,
            refetchInterval: isTagGroupSelected ? LIVE_POLL_INTERVAL_MILLISECONDS : false,
            select,
        }
    );

    const queryParameters = {
        agentStateType: AgentStateType.Offline,
        groupBy: "tag_id",
        startAgentStateUpdateDateTime: serviceLevelAgreementParameters.startDateTime,
    };
    const { data: recentlyOfflineAgents, isLoading: isRecentlyOfflineAgentsLoading } = useAgentCounts(queryParameters, {
        ...options,
        select,
    });

    // Count interactions by agents (that are in the available state) and tag.
    const { data: interactionsByAgentAndTag, isLoading: isInteractionsByAgentAndTagLoading } = useInteractionsCounts<
        TInteractionsByAgent[]
    >(
        {
            agentStateType: [AgentStateType.Available],
            groupBy: ["tag_id", "agent_id"],
            interactionState: [InteractionState.Handling],
            tagGroupId: tagGroupSelectedId,
            ...interactionDateParameters,
        },
        { enabled: isReady && isTagGroupSelected, keepPreviousData: true }
    );

    const interactionsByAgent = groupBy(interactionsByAgentAndTag ?? [], ({ group }) => group[0]);

    // Gather and format all data from requests
    const liveSummaryData: ILiveSummaryData[] = (isTagGroupSelected ? tags ?? [] : []).map((tag) => ({
        ...tag,
        totalConversationsOpen: formatCountData(tag.tagId, totalConversationsOpen),
        activeInteractionsCount: formatCountData(tag.tagId, activeInteractionsCount),
        snoozedInteractionsCount: SHOW_BLOCKED ? formatCountData(tag.tagId, snoozedInteractionsCount) : null,
        queuingInteractionsCount: formatCountData(tag.tagId, queuingInteractionsCount),
        maximumDuration: formatSummariesData(
            tag.tagId,
            ({ maximumHandlingTimeSeconds }) => maximumHandlingTimeSeconds,
            null,
            maximumDuration
        ),
        averageQueueTime: formatSummariesData(
            tag.tagId,
            ({ meanQueueTimeSeconds }) => meanQueueTimeSeconds,
            null,
            averageQueueTime
        ),
        maximumQueueTime: formatSummariesData(
            tag.tagId,
            ({ maximumQueueTimeSeconds }) => maximumQueueTimeSeconds,
            null,
            maximumQueueTime
        ),
        serviceLevelAgreement: formatSummariesData(
            tag.tagId,
            ({ serviceLevelAgreementPercentage }) => serviceLevelAgreementPercentage,
            1,
            serviceLevelAgreement
        ),
        scheduledAgents: null,
        availableAgents: formatCountData(tag.tagId, availableAgents),
        unavailableAgents: formatCountData(tag.tagId, unavailableAgents),
        recentlyOfflineAgents: formatCountData(tag.tagId, recentlyOfflineAgents),
        busiestAgentsConcurrency: interactionsByAgent[tag.tagId]
            ? Math.max(...interactionsByAgent[tag.tagId].map(({ count }) => count))
            : 0,
        averageAgentsConcurrency:
            availableAgents && interactionsByAgent[tag.tagId]
                ? calculateMeanAgentCapacity(availableAgents[tag.tagId]?.count, interactionsByAgent[tag.tagId])
                : 0,
    }));

    const isInteractionsCountLoading =
        isTotalConversationsOpenLoading ||
        isActiveInteractionsCountLoading ||
        (SHOW_BLOCKED && isSnoozedInteractionsCountLoading) ||
        isQueuingInteractionsCountLoading;

    const isMetricsSummariesLoading =
        isMaximumDurationLoading ||
        isAverageQueueTimeLoading ||
        isMaximumQueueTimeLoading ||
        isServiceLevelAgreementLoading;

    const isAgentCountsLoading =
        isAvailableAgentsLoading ||
        isRecentlyOfflineAgentsLoading ||
        isUnavailableAgentsLoading ||
        isInteractionsByAgentAndTagLoading;

    const isLoading =
        (!isReady && isTagGroupSelected) ||
        isTagsLoading ||
        isInteractionsCountLoading ||
        isMetricsSummariesLoading ||
        isAgentCountsLoading;

    return { isLoading, liveSummaryData };
};

export default useLiveSummaryData;
